import { Box, Button, MenuItem, Select, Typography, useTheme } from '@mui/material'
import noAnnouncementsImg from '../../../assets/no-announcements.svg'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Icons, formatDate } from '../../../utils/utilities'
import api from '../../../service/api'
import CreateOrEditAnnouncementDialog from '../../../components/admin/announcements/CreateOrEditAnnouncementDialog'
import TryAgainMessageBlock from '../../../components/common/TryAgainMessageBlock'
import { useOrganizationStore } from '../../../store/organization.store'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import CodeButton from '../../../components/common/CodeButton'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import { useUserStore } from '../../../store/user.store'
import BlockMessage from '../../../components/common/BlockMessage'
import CustomTable from '../../../components/common/custom-table/CustomTable'
import CustomChip from '../../../components/common/CustomChip'

const InstituteList = ( { institutes } ) => {
    return (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap="5px">
            {institutes.map( ( institute, index ) => (
                <CustomChip key={index} value={institute} />
            ) )}
        </Box>
    )
}

const columns = [
    { field: "announcement_title", name: "Title", padding: "10px 20px", minWidth: "200px" },
    { field: "announcement_created_at", renderCell: ( row ) => formatDate( row.announcement_created_at, { month: "short" }, ), name: "Created on", sortable: true, padding: "10px 20px" },
    { field: "announcement_starts_at", renderCell: ( row ) => formatDate( row.announcement_starts_at, { month: "short", showTime: true } ), name: "Duration", padding: "10px 20px" },
    { field: "announcement_status", renderCell: row => <CustomChip value={row.announcement_status} color={row.announcement_status === 'draft' ? "warning" : "success"} textStyles={{ textTransform: "capitalize" }} />, name: "Status", padding: "10px 20px" },
    { field: "target_institutes", renderCell: row => <InstituteList institutes={row.target_institutes || []} />, name: "Target Institutes", filterable: true, padding: "10px 20px" },
    { field: "announcement_for", renderCell: row => <em>{row.announcement_for?.join( ", " )}</em>, name: "For", padding: "10px 20px" },
]

const Announcements = observer( () => {

    const [announcements, setAnnouncements] = useState( [] )
    const [filterInstitute, setFilterInstitute] = useState( "all" )
    const [hasPermission, setHasPermission] = useState( true )
    const [mode, setMode] = useState( 'manage' )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const { palette, border, table, layout } = useTheme()

    const navigate = useNavigate()

    const OrganizationStore = useOrganizationStore()
    const UserStore = useUserStore()

    const getAnnouncements = useCallback( async () => {
        setLoading( true )
        try {
            const { data: { data } } = await api.announcements.fetchAnnouncements( { purpose: 'listing' } )
            setAnnouncements( data )
            setError( null )

        } catch ( err ) {
            setError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching announcements!" } )
        } finally {
            setLoading( false )
        }
    }, [] )

    const filterFunc = announcement => {
        if ( filterInstitute === 'all' ) return true
        return announcement.institute_id?.toUpperCase() === filterInstitute
    }

    const handleCreateOrEditDialogClose = () => {
        setMode( "manage" )
    }

    useEffect( () => {
        if ( UserStore.getUser.user_role === 'SUBADMIN' && !UserStore.getUser.user_permissions.announcements ) {
            setHasPermission( false )
        } else
            getAnnouncements()
    }, [getAnnouncements, UserStore] )

    useEffect( () => {
        const getInstitutes = async () => {
            await OrganizationStore.fetchOrganization()
        }
        if ( OrganizationStore.getOrganization?.length === 0 )
            getInstitutes()
    }, [OrganizationStore] )

    return hasPermission ? (
        <Box padding={layout.pagePadding} overflow="auto" display="flex" flexDirection="column" flexGrow={1}>
            {mode !== "manage" && <CreateOrEditAnnouncementDialog getUpdatedData={getAnnouncements} handleClose={handleCreateOrEditDialogClose} state={mode !== "manage"} />}


            <Box sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                <Box>
                    <Box display="flex" gap="20px" justifyContent="space-between">
                        <Box>
                            <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Announcements</Typography>
                            <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>Manage announcements across the institutes</Typography>
                        </Box>
                        <Box display="flex" gap="10px">
                            <Select sx={{ fontSize: "14px" }} renderValue={() => filterInstitute?.toUpperCase()} value={filterInstitute} size="small" onChange={e => setFilterInstitute( e.target.value )} >
                                <MenuItem value="all" id="all" sx={{ fontSize: "14px" }} dense >
                                    ALL - All institutes
                                </MenuItem>
                                {OrganizationStore.getOrganization.map( institute => (
                                    <MenuItem sx={{ fontSize: "14px" }} value={institute.institute_name_short} key={institute.institute_name_short} id={institute.institute_name_short}>
                                        {institute.institute_name_short} - <Typography textTransform="capitalize" marginLeft="5px" variant='subtitle2' fontSize="12px">{institute.institute_name?.toLowerCase()}</Typography>
                                    </MenuItem>
                                ) )}
                            </Select>
                            <Button size='small' disableElevation sx={{ textTransform: "capitalize" }} variant="contained" startIcon={Icons.default.AddIcon} color="primary" onClick={() => setMode( "create" )}>
                                Create new Announcement
                            </Button>
                        </Box>
                    </Box>
                </Box>
                {!loading && !error && announcements.filter( filterFunc ).length > 0 &&
                    <CustomTable containerStyles={{ border: border[1], borderRadius: layout.borderRadius, marginTop: "20px" }} data={announcements.filter( filterFunc )} pageSize={50} dataIdKey="id" columns={columns} onRowClick={( row ) => navigate( `/admin/announcement/${row.id}` )} />
                }
                {!loading && !error && announcements.filter( filterFunc ).length === 0 &&
                    <Box padding="20px" flexGrow={1} justifyContent="center" display="flex" alignItems="center" flexDirection="column" gap="20px" bgcolor={palette.contentBg}>
                        <img src={noAnnouncementsImg} width="200px" alt="No subadmins added" />
                        <Typography variant='subtitle2' color="textSecondary">No Announcements {filterInstitute !== "all" && "for selected institute"}!</Typography>
                        <Button color='secondary' onClick={() => setMode( "create" )} startIcon={Icons.default.AddIcon} variant='outlined' sx={{ textTransform: "capitalize" }}>Create One</Button>
                    </Box>
                }
                {error && <Box justifyContent="center" display="flex" flexDirection="column" flexGrow={1}>
                    <TryAgainMessageBlock getData={getAnnouncements} err={error.message} code={error.code} loading={loading} />
                </Box>}
                {loading && !error && <DataLoadingSpinner waitingMessage="Loading announcements..." />}
            </Box>
        </Box> ) : ( < Box padding={layout.pagePadding} flexGrow={1} >
            <BlockMessage message={<span>You doesn't have permission to access this page! please contact <i>Alive co-ordinators</i> for further information.</span>} type="error" />
        </Box > )
} )

export default Announcements
